import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { notesApi } from '@/services/notes';
import { VoiceNoteRequest } from '@/types';
import toast from 'react-hot-toast';

interface VoiceRecorderProps {
  meetingId: number;
  meetingDate: string;
  meetingTime: string;
  location: string;
  onComplete?: (noteId: number) => void;
}

export const VoiceRecorder = ({
  meetingId,
  onComplete,
}: VoiceRecorderProps) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcription, setTranscription] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);

        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setIsPaused(false);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      toast.error('Nie można uruchomić mikrofonu');
      console.error('Microphone error:', error);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const handleSubmit = async () => {
    if (!audioBlob) {
      toast.error('Nagraj notatkę głosową');
      return;
    }

    setIsProcessing(true);

    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      reader.onloadend = async () => {
        const base64Audio = reader.result as string;
        const audioBase64 = base64Audio.split(',')[1];

        const noteData: VoiceNoteRequest = {
          meetingId,
          audioBase64,
          duration: recordingTime,
        };

        const response = await notesApi.uploadVoiceNote(noteData);

        if (response.success) {
          setTranscription(response.note.processedContent || response.note.rawContent || '');
          setIsSaved(true);
          toast.success('Notatka głosowa przetworzona przez AI!');

          if (onComplete) {
            onComplete(response.note.id);
          }
        }

        setIsProcessing(false);
      };
    } catch (error) {
      toast.error('Błąd podczas przetwarzania nagrania');
      console.error('Voice note error:', error);
      setIsProcessing(false);
    }
  };

  const handleClear = () => {
    if (window.confirm('Czy na pewno chcesz usunąć nagranie?')) {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
      setAudioBlob(null);
      setAudioUrl(null);
      setRecordingTime(0);
      setTranscription(null);
      setIsSaved(false);
    }
  };

  const handleNewRecording = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setTranscription(null);
    setIsSaved(false);
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <h3 className="text-base sm:text-lg font-semibold text-foreground">Notatki głosowe</h3>
        <div className="text-xs sm:text-sm text-muted-foreground">
          Transkrypcja przez AI
        </div>
      </div>

      {!isSaved ? (
        <>
          <div className="border-2 border-border rounded-lg p-4 sm:p-8 text-center bg-card space-y-4 sm:space-y-6">
            {/* Recording indicator */}
            {isRecording && (
              <div className="flex items-center justify-center gap-2">
                <div className={`w-3 h-3 rounded-full ${isPaused ? 'bg-yellow-500' : 'bg-red-500 animate-pulse'}`}></div>
                <span className="text-base sm:text-lg font-mono">{formatTime(recordingTime)}</span>
              </div>
            )}

            {/* Microphone icon */}
            <div className="text-4xl sm:text-6xl">
              {isRecording ? (isPaused ? '⏸️' : '🔴') : '🎤'}
            </div>

            {/* Recording controls */}
            <div className="flex flex-col sm:flex-row gap-2 justify-center px-2">
              {!isRecording && !audioBlob && (
                <Button onClick={startRecording} size="lg" className="w-full sm:w-auto">
                  Rozpocznij nagrywanie
                </Button>
              )}

              {isRecording && !isPaused && (
                <>
                  <Button onClick={pauseRecording} variant="outline" className="w-full sm:w-auto">
                    Pauza
                  </Button>
                  <Button onClick={stopRecording} variant="destructive" className="w-full sm:w-auto">
                    Zatrzymaj
                  </Button>
                </>
              )}

              {isRecording && isPaused && (
                <>
                  <Button onClick={resumeRecording} className="w-full sm:w-auto">
                    Wznów
                  </Button>
                  <Button onClick={stopRecording} variant="destructive" className="w-full sm:w-auto">
                    Zatrzymaj
                  </Button>
                </>
              )}
            </div>

            {/* Audio playback */}
            {audioUrl && !isRecording && (
              <div className="space-y-3 sm:space-y-4 px-2">
                <audio src={audioUrl} controls className="w-full max-w-md mx-auto" />
                <div className="text-xs sm:text-sm text-muted-foreground">
                  Czas nagrania: {formatTime(recordingTime)}
                </div>
              </div>
            )}
          </div>

          {/* Submit button */}
          {audioBlob && !isProcessing && (
            <div className="flex flex-col gap-2 px-2">
              <Button onClick={handleSubmit} disabled={!audioBlob} className="w-full text-sm sm:text-base py-2 sm:py-2">
                {isProcessing ? 'Przetwarzanie przez AI...' : 'Transkrybuj nagranie'}
              </Button>
              <Button onClick={handleClear} variant="outline" className="w-full text-sm sm:text-base py-2 sm:py-2">
                Usuń nagranie
              </Button>
            </div>
          )}

          {isProcessing && (
            <div className="text-center text-xs sm:text-sm text-muted-foreground px-2">
              <p>Transkrypcja nagrania przez AI...</p>
              <p className="text-xs mt-1">To może potrwać do minuty</p>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Audio player */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-foreground">Nagranie</h4>
              <div className="p-4 bg-card border border-border rounded-md">
                <audio src={audioUrl || ''} controls className="w-full" />
                <p className="text-xs text-muted-foreground mt-2">
                  Czas: {formatTime(recordingTime)}
                </p>
              </div>
            </div>

            {/* Transcription */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                Transkrypcja
                <span className="text-xs text-accent">✓</span>
              </h4>
              <div className="p-4 bg-card border border-border rounded-md h-48 overflow-y-auto">
                <p className="text-sm text-foreground whitespace-pre-wrap">
                  {transcription}
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleNewRecording}>Dodaj kolejne nagranie</Button>
          </div>
        </>
      )}
    </div>
  );
};
